import { useEffect, useRef, type ReactNode } from "react";

/**
 * Tabs with automatic activation. Without JS every panel shows, one
 * after another; the kit's script (site/src/ui/tabs.ts) switches them and fires `ui-tabs-change`.
 */
export function Tabs({
  id,
  tabs,
  value = tabs[0]?.value,
  variant = "underline",
  label,
  className,
  panels,
}: {
  id: string;
  tabs: readonly { value: string; label: string }[];
  value?: string;
  variant?: "underline" | "pill";
  /** Names the tab list for screen readers. */
  label?: string;
  className?: string;
  /** Each tab's panel, by its value. */
  panels: Readonly<Record<string, ReactNode>>;
}) {
  const root = useRef<HTMLDivElement>(null);
  const initialValue = useRef(value);
  const values = tabs.map((tab) => tab.value).join("\0");
  useEffect(() => {
    const element = root.current;
    if (!element) return;
    let cancelled = false;
    void import("../../../src/ui/tabs").then(({ mountTabs, setTabsValue }) => {
      if (cancelled || root.current !== element) return;
      // A setting change applies the authored default; a panel added or moved keeps the choice
      // already on screen when it still exists. Ordinary content edits don't run this effect.
      const selected = [
        ...element.querySelectorAll<HTMLElement>('.ui-tabs-tab[aria-selected="true"]'),
      ].find((tab) => tab.closest('[data-ui="tabs"]') === element)?.dataset.value;
      const desired = initialValue.current !== value ? value : (selected ?? value);
      mountTabs(element);
      setTabsValue(element, desired);
      initialValue.current = value;
    });
    return () => {
      cancelled = true;
    };
  }, [value, values]);
  return (
    <div
      ref={root}
      id={id}
      className={["ui-tabs", className].filter(Boolean).join(" ")}
      data-ui="tabs"
      data-variant={variant}
    >
      <div className="ui-tabs-list" role="tablist" aria-label={label}>
        {tabs.map((tab) => (
          <button
            key={tab.value}
            id={`${id}-tab-${tab.value}`}
            type="button"
            className="ui-tabs-tab"
            role="tab"
            aria-selected={tab.value === value ? "true" : "false"}
            aria-controls={`${id}-panel-${tab.value}`}
            tabIndex={tab.value === value ? 0 : -1}
            data-value={tab.value}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {tabs.map((tab) => (
        <div
          key={tab.value}
          id={`${id}-panel-${tab.value}`}
          className="ui-tabs-panel"
          role="tabpanel"
          aria-labelledby={`${id}-tab-${tab.value}`}
          tabIndex={0}
          hidden={tab.value !== value}
        >
          {panels[tab.value]}
        </div>
      ))}
      <noscript>
        <style>
          {
            ".ui-tabs:not([data-ui-enhanced]) > .ui-tabs-list{display:none}.ui-tabs:not([data-ui-enhanced]) > .ui-tabs-panel[hidden]{display:block}"
          }
        </style>
      </noscript>
    </div>
  );
}
